<template>
  <div
    class="relative single-select"
    ref="container"
    :class="disabledClass"
  >
    <!-- Selected box -->
    <div v-if="!showNothing"
        class="border rounded-lg p-2 text-sm w-full flex justify-between items-center min-h-[40px]"
        :class="[!disabled ? 'cursor-pointer' : '', disabledClass]"
        :style="selectedStyle"
        @click="!disabled && toggleDropdown()"
      >
      <span>{{ selectedLabel }}</span>

      <svg
        v-if="!disabled"
        class="w-4 h-4 ml-2 transform transition-transform"
        :class="{ 'rotate-180': open }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </div>

  </div>

  <Teleport to="body">
    <div
      v-if="open && !disabled"
      ref="dropdown"
      class="fixed z-[11000] border rounded-lg shadow-lg bg-white overflow-auto"
      style="width: max-content"
      :style="dropdownStyle"
      @mousedown.stop
      @click.stop
    >
      <div
        v-for="(opt, index) in sortedOptions"
        :key="opt.id"
        class="p-2 cursor-pointer transition-all duration-150 m-1" 
        :class="{
          // Apply a prominent border/ring and darken the boundary
          'ring-2 ring-gray-800 ring-inset rounded-md font-semibold': opt.id === modelValue,
          'hover:bg-gray-100': opt.id !== modelValue
        }"
        :style="optionStyle(opt, opt.id === modelValue)"
        @click="selectOption(opt)"
      >
        {{ opt.label }}
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { sortDropdownOptions } from '@/utils/globals'

const props = defineProps({
  modelValue: [String, Number],
  options: {
    type: Array,
    required: true
  },
  /** When set (e.g. premium RG status), used to resolve label/colors for values not in `options` (read-only / legacy DB values). */
  displayOptions: {
    type: Array,
    default: null
  },
  fontSize: {
    type: [String, Number],
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  showNothing : {
    type: Boolean,
    default: false
  },
  dimWhenDisabled: {
    type: Boolean,
    default: false
  },
  onUpdate: {
    type: Function,
    default: () => () => {}
  },
  autoOpen: {
    type: Boolean,
    default: false
  },
  preserveOrder: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const dropUp = ref(false)
const dropdownStyle = ref({})

const container = ref(null)
const dropdown = ref(null)

const DROPDOWN_GAP = 4
const VIEWPORT_PADDING = 8
const MAX_DROPDOWN_HEIGHT = 192
const MIN_DROPDOWN_WIDTH = 150

watch(
  () => props.autoOpen,
  (shouldOpen) => {
    if (shouldOpen) openDropdown()
  }
)

// -------------------------
// Selected option
// -------------------------
const sortedOptions = computed(() => sortDropdownOptions(props.options || [], {
  preserveOrder: props.preserveOrder
}))

const selected = computed(() => {
  const opts = sortedOptions.value
  const fromOpts = opts.find(o => o.id == props.modelValue)
  if (fromOpts) return fromOpts
  const disp = props.displayOptions || []
  return disp.find(o => o.id == props.modelValue)
})

const selectedLabel = computed(() => {
  if (selected.value?.label != null && selected.value?.label !== '') return selected.value.label
  // Fallback: if options are missing / value not in options, still show the raw value.
  return props.modelValue == null ? '' : String(props.modelValue)
})

const selectedStyle = computed(() => {
  const opt = selected.value
  return {
    backgroundColor: opt?.bgcolor || 'white',
    color: opt?.color || 'black',
    fontSize: props.fontSize
      ? typeof props.fontSize === 'number'
        ? props.fontSize + 'px'
        : props.fontSize
      : undefined
  }
})

const disabledClass = computed(() => {
  if (props.disabled && props.dimWhenDisabled) {
    // Changed 'cursor-not-allowed' to 'cursor-default'
    return 'opacity-50 cursor-default'
  } else if (props.disabled) {
    // Changed 'cursor-not-allowed' to 'cursor-default'
    return 'cursor-default'
  }
  return ''
})

// -------------------------
// Option styling
// -------------------------
function optionStyle(opt, isSelected = false) {
  return {
    // Keep original color even if selected
    backgroundColor: opt.bgcolor || 'white', 
    color: opt.color || 'black',
    fontSize: props.fontSize
      ? typeof props.fontSize === 'number'
        ? props.fontSize + 'px'
        : props.fontSize
      : undefined
  }
}

function lightenColor(color, amount) {
  try {
    let col = color.startsWith('#')
      ? color.slice(1)
      : color
    if (col.length === 3) col = col.split('').map(c => c + c).join('')
    const num = parseInt(col, 16)
    let r = Math.min(255, (num >> 16) + 255 * amount)
    let g = Math.min(255, ((num >> 8) & 0x00FF) + 255 * amount)
    let b = Math.min(255, (num & 0x0000FF) + 255 * amount)
    return `rgb(${r}, ${g}, ${b})`
  } catch {
    return color
  }
}

// -------------------------
// Dropdown open / close
// -------------------------
function recalcAfterOpen() {
  if (open.value) {
    nextTick(() => {
      requestAnimationFrame(() => {
        recalcDropdownPosition()
      })
    })
  }
}

function openDropdown() {
  if (props.disabled) return
  open.value = true
  recalcAfterOpen()
}

function toggleDropdown() {
  open.value = !open.value
  recalcAfterOpen()
}

function selectOption(opt) {
  if (props.disabled) return
  emit('update:modelValue', opt.id)
  open.value = false
}

// Close on outside click
onClickOutside(container, () => (open.value = false), { ignore: [dropdown] })

// -------------------------
// Auto-flip calculation
// -------------------------
function recalcDropdownPosition() {
  if (!container.value) return

  const rect = container.value.getBoundingClientRect()
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING - DROPDOWN_GAP
  const spaceAbove = rect.top - VIEWPORT_PADDING - DROPDOWN_GAP
  const opensUp = spaceBelow < MAX_DROPDOWN_HEIGHT && spaceAbove > spaceBelow
  const availableHeight = Math.max(80, opensUp ? spaceAbove : spaceBelow)
  const maxHeight = Math.min(MAX_DROPDOWN_HEIGHT, availableHeight)
  const minWidth = Math.max(rect.width, MIN_DROPDOWN_WIDTH)
  const renderedWidth = dropdown.value?.offsetWidth || minWidth
  const left = Math.min(
    Math.max(rect.left, VIEWPORT_PADDING),
    Math.max(VIEWPORT_PADDING, viewportWidth - renderedWidth - VIEWPORT_PADDING)
  )

  dropUp.value = opensUp
  dropdownStyle.value = {
    left: `${left}px`,
    top: opensUp ? 'auto' : `${Math.min(rect.bottom + DROPDOWN_GAP, viewportHeight - VIEWPORT_PADDING - maxHeight)}px`,
    bottom: opensUp ? `${viewportHeight - rect.top + DROPDOWN_GAP}px` : 'auto',
    minWidth: `${minWidth}px`,
    maxWidth: `calc(100vw - ${VIEWPORT_PADDING * 2}px)`,
    maxHeight: `${maxHeight}px`,
    zIndex: 11000
  }
}

// Recalculate on resize + scroll
function handleRecalc() {
  if (open.value) recalcDropdownPosition()
}

onMounted(() => {
  window.addEventListener('resize', handleRecalc)
  window.addEventListener('scroll', handleRecalc, true)
  if (props.autoOpen) openDropdown()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleRecalc)
  window.removeEventListener('scroll', handleRecalc, true)
})
</script>

<style scoped>
/* Optional — helps parent to avoid clipping */
.single-select {
  overflow: visible;
}
</style>
